import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { IconComponent } from '../icon/icon.component';

/** One reading on the bar, as a caption beside its figure: "SHIFTS 20". */
export interface SummaryFigure {
  label: string;
  value: string;
  /** Read on hover, for a figure that has something to add. */
  hint?: string;
}

/** A status pill at the bar's end: the window being read, where it stands, what is narrowing it. */
export interface SummaryBadge {
  label: string;
  tone?: 'default' | 'green' | 'amber';
  icon?: string;
}

/**
 * One-line summary band — a title, divider-separated figures, and status badges at its end. The Shift
 * Roster's week-summary bar as a reusable piece, for a page whose figures are a readout rather than a
 * row of KPI cards. Wraps rather than scrolls: the badges drop to their own line before anything truncates.
 */
@Component({
  selector: 'app-summary-bar',
  standalone: true,
  imports: [IconComponent, NgbTooltipModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="sb">
      @if (title) {
        <span class="sb-title">{{ title }}</span>
      }
      @for (figure of figures; track figure.label; let first = $first) {
        @if (title || !first) {
          <span class="sb-divider" aria-hidden="true"></span>
        }
        <span
          class="sb-figure"
          [class.has-hint]="!!figure.hint"
          [ngbTooltip]="figure.hint || ''"
          [disableTooltip]="!figure.hint"
          container="body"
          placement="bottom auto"
        >
          <span class="sb-figure-label">{{ figure.label }}</span>
          <span class="sb-figure-value">{{ figure.value }}</span>
        </span>
      }
      @if (badges.length) {
        <span class="sb-aside">
          @for (badge of badges; track badge.label) {
            <span class="sb-badge" [attr.data-tone]="badge.tone || 'default'">
              @if (badge.icon) {
                <app-icon [name]="badge.icon" [size]="12" />
              }
              {{ badge.label }}
            </span>
          }
        </span>
      }
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
      }

      .sb {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 8px 4px;
        padding: 12px 16px;
        border: 1px solid var(--h4c-dark-blue-05, rgba(38, 34, 98, 0.05));
        border-radius: 12px;
        background: var(--bg-app, #f6f6fb);
      }

      .sb-title {
        padding-right: 22px;
        font-size: 11px;
        font-weight: var(--fw-bold, 700);
        text-transform: uppercase;
        letter-spacing: 0.09em;
        white-space: nowrap;
        color: var(--h4c-dark-blue, #262262);
      }

      .sb-divider {
        width: 1px;
        height: 20px;
        flex-shrink: 0;
        background: var(--h4c-dark-blue-10, rgba(38, 34, 98, 0.1));
      }

      .sb-figure {
        display: flex;
        align-items: baseline;
        gap: 8px;
        padding: 0 22px;

        &.has-hint {
          cursor: help;
        }
      }

      .sb-figure-label {
        font-size: 10.5px;
        font-weight: var(--fw-bold, 700);
        text-transform: uppercase;
        letter-spacing: 0.08em;
        color: var(--fg-4, #94a3b8);
      }

      .sb-figure-value {
        font-size: 16px;
        font-weight: var(--fw-bold, 700);
        color: var(--h4c-dark-blue, #262262);
        font-feature-settings: 'tnum';
        white-space: nowrap;
      }

      /* The status badges take their own line below the figures, as on the roster's bar at this width. */
      .sb-aside {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 8px;
        flex: 1 0 100%;
      }

      .sb-badge {
        display: inline-flex;
        align-items: center;
        gap: 5px;
        padding: 4px 10px;
        border-radius: var(--radius-pill, 9999px);
        background: var(--bg-card, #fff);
        border: 1px solid var(--border-soft, #e6ebf1);
        font-size: 11px;
        font-weight: var(--fw-bold, 700);
        color: var(--fg-2, #475569);
        white-space: nowrap;

        &[data-tone='amber'] {
          border-color: rgba(245, 158, 11, 0.35);
          background: rgba(245, 158, 11, 0.14);
          color: #b4690e;
        }

        &[data-tone='green'] {
          border-color: rgba(43, 182, 115, 0.3);
          background: rgba(43, 182, 115, 0.12);
          color: #1d7a4d;
        }
      }

      @media (max-width: 560px) {
        .sb-title {
          flex: 1 0 100%;
          padding-right: 0;
        }

        .sb-title + .sb-divider {
          display: none;
        }

        .sb-figure {
          padding: 0 12px 0 0;
        }
      }
    `,
  ],
})
export class SummaryBarComponent {
  @Input() title = '';
  @Input() figures: SummaryFigure[] = [];
  @Input() badges: SummaryBadge[] = [];
}
